(() => {
  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const focusable = qa(focusableSelector, container).filter(el => !el.hidden && el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function setupMobileMenu() {
    const menu = q('[data-mobile-menu]');
    const openButton = q('[data-mobile-open]');
    const closeButton = q('[data-mobile-close]');
    if (!menu || !openButton) return;
    let previousFocus = null;

    const setOpen = state => {
      previousFocus = state ? document.activeElement : previousFocus;
      menu.classList.toggle('is-open', state);
      menu.setAttribute('aria-hidden', String(!state));
      document.body.classList.toggle('no-scroll', state);
      openButton.setAttribute('aria-expanded', String(state));
      if (state) {
        window.requestAnimationFrame(() => closeButton?.focus());
      } else {
        previousFocus?.focus?.();
      }
    };

    openButton.addEventListener('click', () => setOpen(true));
    closeButton?.addEventListener('click', () => setOpen(false));
    qa('a', menu).forEach(link => link.addEventListener('click', () => setOpen(false)));
    menu.addEventListener('keydown', event => trapFocus(menu, event));
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) setOpen(false);
    });
  }

  function setupReveal() {
    const items = qa('.reveal');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach(item => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    }), { threshold: 0.1 });
    items.forEach(item => observer.observe(item));
  }

  function setupLightbox() {
    const lightbox = q('[data-lightbox]');
    const image = q('[data-lightbox-image]');
    const closeButton = q('[data-lightbox-close]', lightbox || document);
    if (!lightbox || !image || !closeButton) return;
    let previousFocus = null;

    const open = trigger => {
      previousFocus = trigger;
      image.src = trigger.dataset.zoom;
      image.alt = q('img', trigger)?.alt || 'Expanded product image';
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      window.requestAnimationFrame(() => closeButton.focus());
    };

    const close = () => {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
      image.removeAttribute('src');
      previousFocus?.focus?.();
    };

    qa('[data-zoom]').forEach(item => item.addEventListener('click', () => open(item)));
    closeButton.addEventListener('click', close);
    lightbox.addEventListener('click', event => { if (event.target === lightbox) close(); });
    lightbox.addEventListener('keydown', event => {
      if (event.key === 'Escape') close();
      trapFocus(lightbox, event);
    });
  }

  function setupProductForms() {
    qa('[data-product-root]').forEach(root => {
      const variantData = q('[data-variants-json]', root);
      if (!variantData) return;
      let variants = [];
      try { variants = JSON.parse(variantData.textContent); } catch (_) { return; }

      const hiddenId = q('[data-variant-id]', root);
      const price = q('[data-product-price]', root);
      const compare = q('[data-compare-price]', root);
      const submit = q('[data-product-submit]', root);
      const status = q('[data-product-status]', root);
      const currency = root.dataset.currency || 'EUR';
      const prelaunch = root.dataset.prelaunch === 'true';
      const comingSoon = root.dataset.comingSoon || 'Coming soon';
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency });
      const selectedValues = () => qa('[data-option-group]', root).map(group => q('input:checked', group)?.value || '');

      const update = () => {
        const values = selectedValues();
        const variant = variants.find(item => item.options.every((option, index) => option === values[index]));
        if (!variant) {
          if (submit) { submit.disabled = true; submit.textContent = 'Unavailable'; }
          if (status) status.textContent = 'This combination is unavailable.';
          return;
        }

        if (hiddenId) hiddenId.value = variant.id;
        if (price) price.textContent = formatter.format(variant.price / 100);
        if (compare) {
          compare.textContent = variant.compare_at_price && variant.compare_at_price > variant.price ? formatter.format(variant.compare_at_price / 100) : '';
          compare.hidden = !compare.textContent;
        }
        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available ? submit.dataset.addText : (prelaunch ? comingSoon : submit.dataset.soldText);
        }
        if (status) {
          status.textContent = variant.available ? '' : (prelaunch ? 'This selection will be available with the forthcoming release.' : 'Sold out in this selection.');
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };

      qa('input[type="radio"]', root).forEach(input => input.addEventListener('change', update));
      update();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    setupMobileMenu();
    setupReveal();
    setupLightbox();
    setupProductForms();
    qa('[data-year]').forEach(element => { element.textContent = new Date().getFullYear(); });
  });
})();
